import {
  AntimalwareAntiExploitApplicationStatus,
  AntimalwareAntiExploitDetectionTechniques,
  AntimalwareAntiExploitSection,
  AntimalwareAntiTamperingSection,
  AntimalwareDynamicThreatDefenseProtectionLevel,
  AntimalwareDynamicThreatDefenseSection,
  AntimalwareOnAccessSection,
  AntimalwareOnDemandScanTask,
  AntimalwareOnDemandSection,
  AntimalwareOnExecuteSection,
  AntimalwareSecurityServersSection,
  AntimalwareSecurityServersSvaIp,
  AntimalwareSettingsDeleteFilesOlderThanType,
  AntimalwareSettingsExclusionsItem,
  AntimlawareSettingsSubmitQuarantinedType,
} from '@nirvana/shared/dtos/types/policies/common';

// UI form value format
export interface AntimalwareAntiTamperingFormValue {
  uiSettings: { antimalware: { antiTampering: AntimalwareAntiTamperingSection } };
}

// UI form value format
export interface AntimalwareOnAccessFormValue {
  uiSettings: { antimalware: { onAccess: AntimalwareOnAccessSection } };
}

// UI form value format
export interface AntimalwareDynamicThreatDefenseFormValue {
  uiSettings: { antimalware: { dynamicThreatDefense: AntimalwareDynamicThreatDefenseSection } };
}

export interface AntimalwareDynamicThreatDefenseProtectionLevelUiValue {
  targetedAttack: AntimalwareDynamicThreatDefenseProtectionLevel;
  suspiciousFilesAndNetworkTraffic: AntimalwareDynamicThreatDefenseProtectionLevel;
  exploits: AntimalwareDynamicThreatDefenseProtectionLevel;
  ransomware: AntimalwareDynamicThreatDefenseProtectionLevel;
  grayware: AntimalwareDynamicThreatDefenseProtectionLevel;
}

// UI form value format
export interface AntimalwareAntiExploitFormValue {
  uiSettings: { antimalware: { antiExploit: AntimalwareAntiExploitSection } };
}

export interface AntimalwareAntiExploitUiApplication {
  applicationName: string;
  processName: string[];
  status: AntimalwareAntiExploitApplicationStatus;
  exploitDetectionTechniques: AntimalwareAntiExploitDetectionTechniques[];
}

// UI form value format
export interface AntimalwareSecurityServersFormValue {
  uiSettings: { antimalware: { securityServers: AntimalwareSecurityServersSection } };
}

export interface AntimalwareSecurityServersSvaIpUiValue extends AntimalwareSecurityServersSvaIp {
  priority: number;
  protectedEntityId: string;
}

// UI form value format
export interface AntimalwareOnExecuteFormValue {
  uiSettings: { antimalware: { onExecute: AntimalwareOnExecuteSection } };
}

// UI form value format
export interface AntimalwareOnDemandFormValue {
  uiSettings: { antimalware: { onDemand: AntimalwareOnDemandSection } };
}

export interface AntimalwareOnDemandScanTaskFormValue extends AntimalwareOnDemandScanTask {
  defaultPositionInGrid: number;
}

// UI form value format
export interface AntimalwareSettingsFormValue {
  uiSettings: {
    antimalware: {
      settings: {
        deleteFilesOlderThan: AntimalwareSettingsDeleteFilesOlderThanType;
        submitQuarantined: {
          enable: boolean;
          hours: AntimlawareSettingsSubmitQuarantinedType;
        };
        rescanQuarantine: boolean;
        copyFilesToQuarantineBeforeDisinfect: boolean;
        allowUserQuarantineActions: boolean;
        centralizedQuarantine?: {
          enable: boolean;
          archivePassword: string;
          location: {
            path: string;
            username: string;
            password: string;
          };
          autoSubmitToSandboxAnalyzer: boolean;
        };
      };
    };
  };
}

// UI form value format
export interface AntimalwareExclusionsFormValue {
  uiSettings: {
    antimalware: {
      settings: {
        useBuiltInExclusions: boolean;
        activateExclusions: {
          enable: boolean;
          exclusionsItems: AntimalwareSettingsExclusionsItem[];
          useVendorExclusionLists: boolean;
          useExclusionLists: boolean;
          selectedVendorExclusionLists: string[];
          exclusionsLists: string[];
        };
      };
    };
  };
}

export enum AntimalwareVendorsExclusionsUiType {
  ALL = 0,
  CUSTOM = 1,
}

// searchable keywords
export enum AntimalwareAntiTamperingSearchableKeywords {
  AntiTampering = 'AntiTampering',
  PreTampering = 'PreTampering',
  VulnerableDrivers = 'VulnerableDrivers',
  PostTampering = 'PostTampering',
  CallbackEvasion = 'CallbackEvasion',
  IsolateEndpoint = 'IsolateEndpoint',
  RebootEndpoint = 'RebootEndpoint',
}
export enum AntimalwareOnAccessSearchableKeywords {
  OnAccess = 'OnAccess',
  FileLocation = 'FileLocation',
  ScanLocalFiles = 'ScanLocalFiles',
  ScanNetworkFiles = 'ScanNetworkFiles',
  Scan = 'Scan',
  OnlyNewOrChangedFiles = 'OnlyNewOrChangedFiles',
  BootSectors = 'BootSectors',
  ProcessMemory = 'ProcessMemory',
  ForKeyloggers = 'ForKeyloggers',
  ForPua = 'ForPua',
  Archives = 'Archives',
  ArchiveMaximumSize = 'ArchiveMaximumSize',
  ArchiveMaximumDepth = 'ArchiveMaximumDepth',
  DeferredScanning = 'DeferredScanning',
  ScanActions = 'ScanActions',
  DefaultAction = 'DefaultAction',
  PuaAction = 'PuaAction',
  NetworkFilesAction = 'NetworkFilesAction',
  OnAccessForLinux = 'OnAccessForLinux',
  OnAccessScanning = 'OnAccessScanning',
}
export enum AntimalwareDynamicThreatDefenseSearchableKeywords {
  HyperDetect = 'HyperDetect',
  ProtectionLevel = 'ProtectionLevel',
  TargetedAttack = 'TargetedAttack',
  SuspiciousFilesAndNetworkTraffic = 'SuspiciousFilesAndNetworkTraffic',
  Exploits = 'Exploits',
  Ransomware = 'Ransomware',
  Grayware = 'Grayware',
  Actions = 'Actions',
  Files = 'Files',
  ExtendReportingOnHigherLevels = 'ExtendReportingOnHigherLevels',
  NetworkTraffic = 'NetworkTraffic',
}
export enum AntimalwareAntiExploitSearchableKeywords {
  AdvancedAntiExploit = 'AdvancedAntiExploit',
  SystemWideDetections = 'SystemWideDetections',
  WindowsDetections = 'WindowsDetections',
  ProcessIntrospection = 'ProcessIntrospection',
  PrivilegeEscalation = 'PrivilegeEscalation',
  LsassProtection = 'LsassProtection',
  LinuxDetections = 'LinuxDetections',
  CredentialsMonitoring = 'CredentialsMonitoring',
  PtraceMonitoring = 'PtraceMonitoring',
  NamespaceMonitoring = 'NamespaceMonitoring',
  CorruptionMonitoring = 'CorruptionMonitoring',
  SuidMonitoring = 'SuidMonitoring',
  PredefinedWindowsApplications = 'PredefinedWindowsApplications',
  CustomWindowsApplications = 'CustomWindowsApplications',
}
export enum AntimalwareSecurityServersSearchableKeywords {
  SecurityServers = 'SecurityServers',
  SecurityServerAssignment = 'SecurityServerAssignment',
  SecurityServerLoadBalancing = 'SecurityServerLoadBalancing',
  RedundancyMode = 'RedundancyMode',
  EqualDistributionMode = 'EqualDistributionMode',
  CommunicationEndpoints = 'CommunicationEndpoints',
  UseSsl = 'UseSsl',
  CommunicationGravityZone = 'CommunicationGravityZone',
  KeepInstallation = 'KeepInstallation',
  UseProxy = 'UseProxy',
  DoNotUseProxy = 'DoNotUseProxy',
  SecurityServerConfiguration = 'SecurityServerConfiguration',
  Limit = 'Limit',
}
export enum AntimalwareOnExecuteSearchableKeywords {
  OnExecuteScanning = 'OnExecuteScanning',
  CloudBasedThreatDetection = 'CloudBasedThreatDetection',
  AdvancedThreatControl = 'AdvancedThreatControl',
  SensitiveProtection = 'SensitiveProtection',
  KernelApi = 'KernelApi',
  DefaultAction = 'DefaultAction',
  Aggressive = 'Aggressive',
  Normal = 'Normal',
  Permissive = 'Permissive',
  FilelessAttackProtection = 'FilelessAttackProtection',
  CommandLineScanner = 'CommandLineScanner',
  AntimalwareScanInterface = 'AntimalwareScanInterface',
  Report = 'Report',
  Ransomware = 'Ransomware',
  Efs = 'Efs',
  Monitor = 'Monitor',
  Locally = 'Locally',
  Remote = 'Remote',
  Recover = 'Recover',
  OnDemand = 'OnDemand',
  Automatically = 'Automatically',
}
export enum AntimalwareOnDemandSearchableKeywords {
  OnDemand = 'OnDemand',
  ScanTasks = 'ScanTasks',
  ScanSettings = 'ScanSettings',
  ContextualScan = 'ContextualScan',
  ExternalDevicesScan = 'ExternalDevicesScan',
  DeviceScanning = 'DeviceScanning',
  CdDvdMedia = 'CdDvdMedia',
  UsbStorageDevices = 'UsbStorageDevices',
  DoNotScan = 'DoNotScan',
}

export enum AntimalwareSettingsSearchableKeywords {
  Settings = 'Settings',
  Quarantine = 'Quarantine',
  DeleteFilesOlderThan = 'DeleteFilesOlderThan',
  SubmitQuarantined = 'SubmitQuarantined',
  RescanQuarantine = 'RescanQuarantine',
  CopyFilesToQuarantine = 'CopyFilesToQuarantine',
  AllowUserQuarantineActions = 'AllowUserQuarantineActions',
}

export enum AntimalwareExclusionsSearchableKeywords {
  Exclusions = 'Exclusions',
  InPolicyExclusions = 'InPolicyExclusions',
  ExclusionsFromConfigurationProfiles = 'ExclusionsFromConfigurationProfiles',
  VendorExclusions = 'VendorExclusions',
}
